// Component section for System Architecture - 198 lines ✅
import { Code, Server, Brain, Layers } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { ComponentGraph } from './ComponentGraph'

interface ComponentGroup {
  title: string
  icon: React.ElementType
  items: ComponentItem[]
  color: string
}

interface ComponentItem {
  name: string
  description: string
  count?: string
}

const frontendComponents: ComponentGroup = {
  title: 'Frontend Components (React/TypeScript)',
  icon: Code,
  color: 'bg-blue-50 border-blue-300',
  items: [
    { name: 'Pages', description: 'Route-level page components', count: '9' },
    { name: 'Layout', description: 'AppLayout, Header, Sidebar, ErrorBoundary', count: '4' },
    { name: 'Issue Recording', description: 'IssueRecordingChat, QuickReplyButtons, ConversationPanel', count: '8' },
    { name: 'Knowledge Base', description: 'KBSearchBar, KBResultCard, KBEntryCard, SearchFilters', count: '12' },
    { name: 'Knowledge Graph', description: 'KnowledgeGraphView, KGNodeComponent, NodeDetailPanel', count: '3' },
    { name: 'Documents', description: 'DocumentUpload, DocumentList, DocumentViewer', count: '6' },
    { name: 'UI Primitives', description: 'Button, Alert, Tabs, Badge, Select, etc.', count: '14+' }
  ]
}

const backendComponents: ComponentGroup = {
  title: 'Backend Components (Supabase)',
  icon: Server,
  color: 'bg-green-50 border-green-300',
  items: [
    { name: 'Database Tables', description: 'PostgreSQL tables with RLS', count: '33' },
    { name: 'Edge Functions', description: 'Serverless TypeScript functions', count: '13' },
    { name: 'Shared Modules', description: 'supabase, tenant, validation, agent-logger', count: '7' },
    { name: 'Triggers', description: 'Database triggers for auto-sync', count: '20+' },
    { name: 'RLS Policies', description: 'Row-level security policies', count: '150+' },
    { name: 'Indexes', description: 'Performance indexes including HNSW', count: '80+' }
  ]
}

const aiComponents: ComponentGroup = {
  title: 'AI/ML Components (Kaycoo Platform)',
  icon: Brain,
  color: 'bg-purple-50 border-purple-300',
  items: [
    { name: 'd365-issue-recorder', description: 'Conversational issue extraction using NLP' },
    { name: 'd365-kb-search', description: 'Semantic similarity search with pgvector' },
    { name: 'd365-kb-manager', description: 'Categorization, duplicate detection, quality scoring' },
    { name: 'd365-innovation-orchestrator', description: 'Multi-step innovation analysis' },
    { name: 'Vector Search', description: 'pgvector HNSW + cosine similarity', count: '1536-dim' },
    { name: 'Embedding Model', description: 'text-embedding-3-small (OpenAI)' }
  ]
}

const hookComponents: ComponentGroup = {
  title: 'Custom React Hooks',
  icon: Layers,
  color: 'bg-orange-50 border-orange-300',
  items: [
    { name: 'useAuth', description: 'Authentication state management' },
    { name: 'useKBSearch', description: 'Knowledge base search with caching' },
    { name: 'useKnowledgeGraph', description: 'Knowledge graph data fetching' },
    { name: 'useIssues', description: 'Issue CRUD operations' },
    { name: 'useConversation', description: 'Chat session management' }
  ]
}

const edgeFunctions = [
  { name: 'issue-recorder', category: 'Core AI', description: 'Conversational issue capture' },
  { name: 'kb-search', category: 'Core AI', description: 'Semantic + full-text search' },
  { name: 'kb-manager', category: 'Core AI', description: 'KB categorization & quality' },
  { name: 'innovation-orchestrator', category: 'Core AI', description: 'Innovation analysis' },
  { name: 'expert-router', category: 'Routing', description: 'Route issues to experts' },
  { name: 'solution-validator', category: 'Routing', description: 'Validate solutions' },
  { name: 'upload-document', category: 'Documents', description: 'File upload handler' },
  { name: 'process-document', category: 'Documents', description: 'Text extraction & chunking' },
  { name: 'search-documents', category: 'Documents', description: 'Document semantic search' },
  { name: 'external-doc-search', category: 'Documents', description: 'External KB search' },
  { name: 'send-email', category: 'Utilities', description: 'Email notifications (Resend)' },
  { name: 'health', category: 'Utilities', description: 'Health check endpoint' },
  { name: 'ready', category: 'Utilities', description: 'Readiness probe' }
]

function ComponentGroupCard({ group }: { group: ComponentGroup }) {
  const Icon = group.icon

  return (
    <div className={`rounded-lg border-2 p-4 ${group.color}`}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-6 w-6 text-kaycoo-teal-dark" />
        <h3 className="text-lg font-semibold text-kaycoo-teal-dark">{group.title}</h3>
      </div>
      <div className="space-y-2">
        {group.items.map((item, i) => (
          <div key={i} className="rounded bg-white p-2">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="font-medium text-kaycoo-teal-dark">{item.name}</div>
                <div className="text-xs text-kaycoo-charcoal-2">{item.description}</div>
              </div>
              {item.count && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {item.count}
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function EdgeFunctionsList() {
  const categories = [...new Set(edgeFunctions.map(f => f.category))]

  return (
    <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
      <div className="mb-4 flex items-center gap-2">
        <Server className="h-6 w-6 text-kaycoo-teal-dark" />
        <h3 className="text-xl font-semibold text-kaycoo-teal-dark">Edge Functions (13 Deployed)</h3>
      </div>
      <div className="space-y-4">
        {categories.map(category => (
          <div key={category}>
            <h4 className="mb-2 text-sm font-semibold text-kaycoo-teal-dark">{category}</h4>
            <div className="grid gap-2 md:grid-cols-2">
              {edgeFunctions
                .filter(f => f.category === category)
                .map(func => (
                  <div key={func.name} className="rounded border border-kaycoo-teal bg-kaycoo-teal-pale p-2">
                    <div className="font-mono text-sm font-medium text-kaycoo-teal-dark">{func.name}</div>
                    <div className="text-xs text-kaycoo-charcoal-2">{func.description}</div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function ComponentSection() {
  return (
    <div className="space-y-6">
      {/* Interactive Graph */}
      <ComponentGraph />

      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <h2 className="mb-4 text-2xl font-bold text-kaycoo-teal-dark">Application Components (Detailed View)</h2>
        <p className="text-kaycoo-charcoal-2">
          Complete breakdown of frontend components, backend services, AI agents, and custom hooks.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ComponentGroupCard group={frontendComponents} />
        <ComponentGroupCard group={backendComponents} />
        <ComponentGroupCard group={aiComponents} />
        <ComponentGroupCard group={hookComponents} />
      </div>

      <EdgeFunctionsList />

      <div className="rounded-lg border border-kaycoo-teal-dark bg-kaycoo-teal-pale p-6">
        <h3 className="mb-3 text-lg font-semibold text-kaycoo-teal-dark">Technology Stack Summary</h3>
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <div className="text-sm font-medium text-kaycoo-teal-dark">Frontend</div>
            <div className="text-xs text-kaycoo-charcoal-2">React 18.2, TypeScript 5.3, Vite 5.0, TailwindCSS 3.4</div>
          </div>
          <div>
            <div className="text-sm font-medium text-kaycoo-teal-dark">Backend</div>
            <div className="text-xs text-kaycoo-charcoal-2">Supabase (PostgreSQL 15+, Edge Functions, Auth)</div>
          </div>
          <div>
            <div className="text-sm font-medium text-kaycoo-teal-dark">AI/ML</div>
            <div className="text-xs text-kaycoo-charcoal-2">Kaycoo Platform, OpenAI GPT-4, text-embedding-3-small</div>
          </div>
        </div>
      </div>
    </div>
  )
}
